import { useEffect, useState } from "react";
import { STRINGS } from "@/lib/i18n";
import {
  adminListLanguages, adminAddLanguage, adminUpdateLanguage, adminDeleteLanguage, adminTranslateUi,
  adminTranslateAll, adminTranslateAllStatus, adminPublishAll, adminSiteTranslate, adminSiteTranslateStatus,
} from "@/lib/api";
import { toast } from "sonner";
import { Languages as LanguagesIcon, Sparkles, Trash2, Eye, EyeOff, Loader2, Plus, Wand2, CheckCheck, Globe } from "lucide-react";

export default function AdminLanguages() {
  const [langs, setLangs] = useState([]);
  const [form, setForm] = useState({ code: "", label: "", native_label: "" });
  const [busy, setBusy] = useState("");
  const [bulk, setBulk] = useState({});
  const [site, setSite] = useState({});

  const load = () => adminListLanguages().then((all) => {
    setLangs(all);
    // Resume any bulk / site job already running (e.g. started in another tab).
    all.forEach((l) => {
      if (!l.system) {
        adminTranslateAllStatus(l.code).then((s) => {
          if (s?.running) setBulk((b) => ({ ...b, [l.code]: s }));
        }).catch(() => {});
      }
      if (l.code !== "en") {
        adminSiteTranslateStatus(l.code).then((s) => {
          if (s?.running) setSite((b) => ({ ...b, [l.code]: s }));
        }).catch(() => {});
      }
    });
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  // Poll site-wide translation progress.
  useEffect(() => {
    const running = Object.entries(site).filter(([, v]) => v?.running).map(([c]) => c);
    if (running.length === 0) return;
    const id = setInterval(async () => {
      for (const c of running) {
        try {
          const s = await adminSiteTranslateStatus(c);
          setSite((b) => ({ ...b, [c]: s }));
          if (!s.running) { toast.success(`Site translated into ${c.toUpperCase()} — ${s.done - (s.failed || 0)} items${s.failed ? ` (${s.failed} failed)` : ""}`); load(); }
        } catch { /* ignore */ }
      }
    }, 3000);
    return () => clearInterval(id);
  }, [site]);

  const siteTranslate = async (l) => {
    if (!window.confirm(`Auto-translate the ENTIRE site (all content, temples, deities and hub) into ${l.label}? This runs in the background and may take a few minutes.`)) return;
    try {
      await adminSiteTranslate(l.code, false);
      setSite((b) => ({ ...b, [l.code]: { running: true, total: 0, done: 0, failed: 0 } }));
      toast.success(`Site-wide translation into ${l.label} started`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start translation."); }
  };

  // Poll bulk-translation progress for any language currently running.
  useEffect(() => {
    const running = Object.entries(bulk).filter(([, v]) => v?.running).map(([c]) => c);
    if (running.length === 0) return;
    const id = setInterval(async () => {
      for (const c of running) {
        try {
          const s = await adminTranslateAllStatus(c);
          setBulk((b) => ({ ...b, [c]: s }));
          if (!s.running) { toast.success(`Translated ${s.done - (s.failed || 0)} entries into ${c}${s.failed ? ` (${s.failed} failed)` : ""}`); load(); }
        } catch { /* ignore */ }
      }
    }, 2500);
    return () => clearInterval(id);
  }, [bulk]);

  const translateAll = async (l) => {
    if (!window.confirm(`Auto-translate ALL entries into ${l.label}? This runs in the background and creates drafts you can review and publish.`)) return;
    try {
      await adminTranslateAll(l.code, false);
      setBulk((b) => ({ ...b, [l.code]: { running: true, total: 0, done: 0, failed: 0 } }));
      toast.success(`Bulk translation into ${l.label} started`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start bulk translation."); }
  };

  const publishAll = async (l) => {
    if (!window.confirm(`Publish ALL translated entries in ${l.label} to the public site?`)) return;
    const r = await adminPublishAll(l.code, true);
    toast.success(`Published ${r.updated} entries in ${l.label}`);
    load();
  };

  const add = async () => {
    if (!form.code.trim() || !form.label.trim()) { toast.error("Enter a code and a name."); return; }
    try {
      await adminAddLanguage(form);
      toast.success(`Added ${form.label}`);
      setForm({ code: "", label: "", native_label: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add language."); }
  };

  const toggleEnabled = async (l) => {
    await adminUpdateLanguage(l.code, { enabled: !l.enabled });
    toast.success(!l.enabled ? `${l.label} is now live on the site` : `${l.label} hidden from the site`);
    load();
  };

  const translateUi = async (l) => {
    setBusy(l.code + "-ui");
    try {
      await adminTranslateUi(l.code, STRINGS.en);
      toast.success(`Menus & labels translated into ${l.label}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "UI translation failed."); }
    finally { setBusy(""); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete ${l.label} and all its translations? This cannot be undone.`)) return;
    await adminDeleteLanguage(l.code);
    toast.success(`Deleted ${l.label}`);
    load();
  };

  return (
    <div className="mx-auto max-w-4xl" data-testid="admin-languages">
      <div className="mb-2 flex items-center gap-3">
        <LanguagesIcon className="h-6 w-6 text-saffron" />
        <h1 className="font-serif-latin text-3xl text-ink">Languages</h1>
      </div>
      <p className="mb-8 text-sm text-ink-3">Add any language, auto-translate the menus/labels and content into it with AI, review, then make it live. New languages stay hidden from visitors until you enable them.</p>

      {/* Add language */}
      <div className="mb-10 border border-line bg-surface p-5 sm:p-6">
        <p className="overline-label mb-4 text-gold">Add a language</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <input data-testid="lang-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Code (e.g. ta, bn, te)" className="border border-line bg-parchment px-3 py-2.5 text-sm text-ink focus:border-saffron focus:outline-none" />
          <input data-testid="lang-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="English name (e.g. Tamil)" className="border border-line bg-parchment px-3 py-2.5 text-sm text-ink focus:border-saffron focus:outline-none" />
          <input data-testid="lang-native" value={form.native_label} onChange={(e) => setForm({ ...form, native_label: e.target.value })} placeholder="Native name (e.g. தமிழ்)" className="border border-line bg-parchment px-3 py-2.5 text-sm text-ink focus:border-saffron focus:outline-none" />
        </div>
        <button data-testid="lang-add" onClick={add} className="mt-4 inline-flex items-center gap-2 bg-[var(--saffron)] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
          <Plus className="h-4 w-4" /> Add language
        </button>
      </div>

      {/* List */}
      <div className="space-y-3">
        {langs.map((l) => (
          <div key={l.code} data-testid={`lang-row-${l.code}`} className="flex flex-wrap items-center justify-between gap-4 border border-line bg-surface p-5">
            <div>
              <p className="font-serif-latin text-lg text-ink">
                {l.native_label || l.label} <span className="text-ink-3">· {l.code}</span>
                {l.system && <span className="ml-2 text-[10px] uppercase tracking-wide text-ink-3">built-in</span>}
                {!l.system && (l.enabled
                  ? <span className="ml-2 text-[10px] uppercase tracking-wide text-green-700">live</span>
                  : <span className="ml-2 text-[10px] uppercase tracking-wide text-gold">admin-only</span>)}
              </p>
              {!l.system && <p className="mt-0.5 text-xs text-ink-3">{l.published} published · {l.drafts} drafts · {l.ui_count} UI labels</p>}
              {site[l.code]?.running && (
                <div className="mt-2 w-56" data-testid={`site-progress-${l.code}`}>
                  <div className="mb-1 flex items-center gap-1.5 text-xs text-gold"><Loader2 className="h-3 w-3 animate-spin" /> Translating site {site[l.code].done}/{site[l.code].total || "…"}</div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-alt)]">
                    <div className="h-full rounded-full bg-[var(--saffron)] transition-all" style={{ width: `${site[l.code].total ? Math.round((site[l.code].done / site[l.code].total) * 100) : 5}%` }} />
                  </div>
                </div>
              )}
              {!l.system && bulk[l.code]?.running && (
                <div className="mt-2 w-56" data-testid={`bulk-progress-${l.code}`}>
                  <div className="mb-1 flex items-center gap-1.5 text-xs text-gold"><Loader2 className="h-3 w-3 animate-spin" /> Translating {bulk[l.code].done}/{bulk[l.code].total || "…"}</div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-alt)]">
                    <div className="h-full rounded-full bg-[var(--saffron)] transition-all" style={{ width: `${bulk[l.code].total ? Math.round((bulk[l.code].done / bulk[l.code].total) * 100) : 5}%` }} />
                  </div>
                </div>
              )}
            </div>
            {l.code !== "en" && (
              <div className="flex flex-wrap items-center gap-2">
                <button data-testid={`lang-site-translate-${l.code}`} onClick={() => siteTranslate(l)} disabled={site[l.code]?.running} className="inline-flex items-center gap-1.5 border border-[var(--gold-muted)] bg-[rgba(199,80,0,0.06)] px-3 py-2 text-xs font-medium text-saffron transition-colors hover:border-saffron disabled:opacity-50">
                  {site[l.code]?.running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />} Translate entire site
                </button>
              </div>
            )}
            {!l.system && (
              <div className="flex flex-wrap items-center gap-2">
                <button data-testid={`lang-translate-all-${l.code}`} onClick={() => translateAll(l)} disabled={bulk[l.code]?.running} className="inline-flex items-center gap-1.5 border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.06)] px-3 py-2 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-50">
                  <Wand2 className="h-3.5 w-3.5" /> Translate all entries
                </button>
                <button data-testid={`lang-publish-all-${l.code}`} onClick={() => publishAll(l)} className="inline-flex items-center gap-1.5 border border-line px-3 py-2 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
                  <CheckCheck className="h-3.5 w-3.5" /> Publish all
                </button>
                <button data-testid={`lang-translate-ui-${l.code}`} onClick={() => translateUi(l)} disabled={busy === l.code + "-ui"} className="inline-flex items-center gap-1.5 border border-line px-3 py-2 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-50">
                  {busy === l.code + "-ui" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Translate menus
                </button>
                <button data-testid={`lang-toggle-${l.code}`} onClick={() => toggleEnabled(l)} className="inline-flex items-center gap-1.5 border border-line px-3 py-2 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
                  {l.enabled ? <><EyeOff className="h-3.5 w-3.5" /> Hide</> : <><Eye className="h-3.5 w-3.5" /> Make live</>}
                </button>
                <button data-testid={`lang-delete-${l.code}`} onClick={() => remove(l)} className="inline-flex items-center gap-1.5 border border-line px-3 py-2 text-xs text-red-700 transition-colors hover:border-red-700">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <p className="mt-8 text-xs text-ink-3">Tip: to translate the reading content into a language, open any entry in the Content Library and use the “Translations” panel to auto-translate, review and publish it.</p>
    </div>
  );
}
